import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, X, Layers, Search as SearchIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { searchImages } from "@/api";
import { cn, prettify } from "@/lib/utils";

export default function ComparePage() {
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState([null, null]); // image objects

  useEffect(() => {
    setLoading(true);
    searchImages({})
      .then((res) => setLibrary(res.results || []))
      .catch(() => setLibrary([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!q) return library;
    const needle = q.toLowerCase();
    return library.filter((img) => {
      const hay = [
        img.title,
        img.notes,
        img.room_type,
        img.style,
        ...(img.tags || []),
        ...(img.materials || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [library, q]);

  function pickImage(img) {
    setSelected((prev) => {
      // If already selected on a slot, deselect
      const idx = prev.findIndex((p) => p && p.id === img.id);
      if (idx !== -1) {
        const next = [...prev];
        next[idx] = null;
        return next;
      }
      // Otherwise fill the first empty slot
      const empty = prev.findIndex((p) => !p);
      if (empty === -1) {
        // both full → replace second
        return [prev[1], img];
      }
      const next = [...prev];
      next[empty] = img;
      return next;
    });
  }

  function clearSlot(idx) {
    setSelected((prev) => prev.map((p, i) => (i === idx ? null : p)));
  }

  function clearAll() {
    setSelected([null, null]);
  }

  const filled = selected.filter(Boolean).length;

  return (
    <div
      className="max-w-[1600px] mx-auto px-6 md:px-10 py-10 md:py-14"
      data-testid="compare-page"
    >
      <div className="mb-8 md:mb-12 flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold mb-2">
            Side by side
          </p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-stone-900">
            Compare two references
          </h1>
          <p className="mt-3 text-stone-500 max-w-lg">
            Pick any two images from your library — materials, palettes and
            styles, instantly side-by-side.
          </p>
        </div>
        {filled > 0 && (
          <Button
            onClick={clearAll}
            data-testid="clear-comparison-button"
            className="rounded-full bg-stone-100 text-stone-900 hover:bg-stone-200"
          >
            Clear comparison
          </Button>
        )}
      </div>

      {/* Comparison Stage */}
      <div
        className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-10"
        data-testid="compare-stage"
      >
        {selected.map((img, idx) => (
          <ComparePane
            key={idx}
            slotIndex={idx}
            image={img}
            onClear={() => clearSlot(idx)}
          />
        ))}
      </div>

      {/* Library Picker */}
      <div className="border-t border-stone-100 pt-8">
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold mb-1">
              Your library
            </p>
            <h2 className="font-display text-2xl text-stone-900">
              Tap an image to add to comparison
            </h2>
          </div>
          <span className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500">
            {filled} / 2 selected
          </span>
        </div>

        <div className="relative mb-6 max-w-md">
          <SearchIcon
            className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400"
            strokeWidth={1.5}
          />
          <Input
            data-testid="compare-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search your library…"
            className="pl-11 h-11 rounded-full border-stone-200 bg-stone-50 focus-visible:bg-white"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-stone-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading library…
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="border border-dashed border-stone-200 rounded-2xl py-20 text-center"
            data-testid="compare-empty-state"
          >
            <div className="w-14 h-14 rounded-full bg-stone-100 mx-auto flex items-center justify-center mb-5">
              <Layers className="w-6 h-6 text-stone-400" strokeWidth={1.5} />
            </div>
            <h3 className="font-display text-2xl text-stone-900">
              Nothing to compare yet
            </h3>
            <p className="text-stone-500 mt-2 max-w-md mx-auto text-sm">
              Upload references into any project — then come back here to put
              two side by side.
            </p>
          </div>
        ) : (
          <div
            className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-3"
            data-testid="compare-library-grid"
          >
            {filtered.map((img, i) => {
              const isSelected = selected.some((s) => s && s.id === img.id);
              return (
                <motion.button
                  key={img.id}
                  type="button"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.35,
                    delay: Math.min(i * 0.025, 0.4),
                  }}
                  onClick={() => pickImage(img)}
                  data-testid={`compare-pick-${img.id}`}
                  className={cn(
                    "relative aspect-square rounded-lg overflow-hidden bg-stone-100 group transition-all",
                    isSelected
                      ? "ring-2 ring-stone-900 ring-offset-2 ring-offset-white"
                      : "hover:ring-1 hover:ring-stone-300 hover:ring-offset-2"
                  )}
                >
                  <img
                    src={img.data}
                    alt={img.title || img.filename}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {isSelected && (
                    <div className="absolute inset-0 bg-stone-900/30 flex items-center justify-center">
                      <span className="text-white text-xs uppercase tracking-[0.18em] font-semibold bg-stone-900 px-2.5 py-1 rounded-full">
                        Selected
                      </span>
                    </div>
                  )}
                </motion.button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function ComparePane({ slotIndex, image, onClear }) {
  if (!image) {
    return (
      <div
        data-testid={`compare-slot-${slotIndex}-empty`}
        className="aspect-[4/5] rounded-2xl border-2 border-dashed border-stone-200 flex flex-col items-center justify-center bg-stone-50/50"
      >
        <div className="w-12 h-12 rounded-full bg-white border border-stone-200 flex items-center justify-center mb-3">
          <Layers className="w-5 h-5 text-stone-400" strokeWidth={1.5} />
        </div>
        <p className="text-stone-500 text-sm">
          Slot {slotIndex + 1} — pick an image below
        </p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      data-testid={`compare-slot-${slotIndex}-filled`}
      className="relative rounded-2xl overflow-hidden bg-stone-100 group"
    >
      <div className="aspect-[4/5] bg-stone-100 flex items-center justify-center">
        <img
          src={image.data}
          alt={image.title || image.filename}
          className="w-full h-full object-cover"
        />
      </div>
      <button
        type="button"
        onClick={onClear}
        data-testid={`compare-slot-${slotIndex}-clear`}
        className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 backdrop-blur flex items-center justify-center text-stone-700 hover:text-red-600 shadow-sm"
      >
        <X className="w-4 h-4" strokeWidth={1.5} />
      </button>

      {/* Metadata overlay */}
      <div className="absolute bottom-0 left-0 right-0 p-5 pt-12 bg-gradient-to-t from-stone-900/90 via-stone-900/50 to-transparent text-white">
        {image.title && (
          <p className="font-display text-lg leading-tight mb-2">
            {image.title}
          </p>
        )}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
          {image.room_type && (
            <span>
              <span className="text-white/60">Room</span>{" "}
              {prettify(image.room_type)}
            </span>
          )}
          {image.style && (
            <span>
              <span className="text-white/60">Style</span>{" "}
              {prettify(image.style)}
            </span>
          )}
        </div>
        {image.dominant_colors?.length > 0 && (
          <div
            className="flex gap-1.5 mt-3"
            data-testid={`compare-slot-${slotIndex}-palette`}
          >
            {image.dominant_colors.slice(0, 6).map((c, i) => (
              <span
                key={i}
                className="w-6 h-6 rounded-full border border-white/30"
                style={{ background: c }}
                title={c}
              />
            ))}
          </div>
        )}
        {image.materials?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {image.materials.slice(0, 5).map((m) => (
              <span
                key={m}
                className="bg-white/15 text-white text-[10px] px-2.5 py-1 rounded-full backdrop-blur"
              >
                {prettify(m)}
              </span>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}
